import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react'
import { supabase } from '../lib/supabaseClient'

// --- Types ---
export type Profile = {
  id: string
  full_name?: string
  email?: string
  phone?: string
  address_line1?: string
  address_line2?: string
  zip_code?: string
  city?: string
  country?: string
  default_racket_brand?: string
  default_racket_description?: string
  default_tension_vertical?: number
  default_tension_horizontal?: number
  role?: 'USER' | 'ADMIN' | 'PARTNER'
  partner_id?: string | null
  partner_role?: 'ADMIN_PARTNER' | 'USER_PARTNER' | null
}

type AuthContextType = {
  user: any
  profile: Profile | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (data: {
    name: string
    email: string
    password: string
    phone?: string
    address?: string
  }) => Promise<void>
  logout: () => Promise<void>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// --- Provider ---
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)

  // ✅ Evite double init en dev (React.StrictMode)
  const didInitRef = useRef(false)

  // Charger le profil utilisateur (non bloquant côté listener)
  const loadProfile = async (userId: string) => {
    // petit garde : si userId vide
  
    if (!userId) return
  
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single<Profile>()

    if (error) {
      console.warn('Profil non trouvé pour user', userId, error)
      setProfile(null)
      return
    }
    setProfile(data)
  }

  useEffect(() => {
    let alive = true

    setLoading(true)

    const { data: listener } = supabase.auth.onAuthStateChange(async (event, session) => {
      setTimeout(async () => {
        if (!alive) return

        // Cet event arrive au boot avec la session si elle existe
        if (event === 'INITIAL_SESSION') {
          if (session?.user) {
            // Verify the session is still valid by attempting a lightweight call
            const { error: refreshError } = await supabase.auth.getUser()
            if (refreshError) {
              // Session is stale (e.g. after DB reset) — purge local tokens
              console.warn('Session invalide détectée, déconnexion locale:', refreshError.message)
              await supabase.auth.signOut({ scope: 'local' })
              setUser(null)
              setProfile(null)
              setLoading(false)
              return
            }
            setUser(session.user)
            await loadProfile(session.user.id)
          } else {
            setUser(null)
          }
          setLoading(false)
          return
        }

        console.log("Auth event:", event, session);
        if (event === 'SIGNED_IN' && session?.user) {
          setUser(session.user)
          await loadProfile(session.user.id)
          setLoading(false)
          return
        }

        if (event === 'SIGNED_OUT') {
          setUser(null)
          setProfile(null)
          setLoading(false)
          return
        }

        // fallback : si autre event, ne pas bloquer
        setLoading(false)
      }, 0)
    })

    return () => {
      alive = false
      listener.subscription.unsubscribe()
    }
  }, [])


  const refreshProfile = async () => {
    if (user?.id) await loadProfile(user.id)
  }

  // --- Méthodes Auth ---
  const login = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw new Error(error.message)

    // ✅ Ne pas appeler getUser() (inutile + parfois source d’effets de bord)
    const u = data.session?.user ?? data.user ?? null
    setUser(u)
    if (u?.id) await loadProfile(u.id)
  }

  const register = async ({ name, email, password, phone, address }: {
    name: string
    email: string
    password: string
    phone?: string
    address?: string
  }) => {
    const { data, error: signupError } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: name } },
    })

    if (signupError) throw new Error(signupError.message)
    if (!data.user) throw new Error('Erreur lors de la création du compte.')

    // Crée le profil enrichi
    const { error: profileError } = await supabase.from('profiles').upsert({
      id: data.user.id,
      full_name: name,
      email: email,
      phone: phone || null,
      address_line1: address || null,
      role: 'USER',
    })

    if (profileError) console.error('Erreur création profil:', profileError)

    // ⚠️ Si confirmation email requise, il n’y aura pas forcément de session ici.
    // On met quand même user en mémoire si présent.
    setUser(data.user)
    await loadProfile(data.user.id)
  }

  const logout = async () => {
    // Use scope: 'local' to avoid session_not_found errors when
    // the server-side session no longer exists (e.g. after DB reset).
    await supabase.auth.signOut({ scope: 'local' })
    setUser(null)
    setProfile(null)
  }

  const value: AuthContextType = {
    user,
    profile,
    loading,
    login,
    register,
    logout,
    refreshProfile,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// --- Hook Helper ---
export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
